import type { AllComponents } from "../interfaces/base";

type TemplateLike = {
  id?: number;
  name?: string;
  component?: AllComponents | null;
  rootComponentUuid?: string | null;
  components?: Record<string, AllComponents> | null;
};

function toTemplateChildren(source: any): any[] {
  if (Array.isArray(source?.childElements)) {
    return source.childElements;
  }
  if (Array.isArray(source?.child_elements)) {
    return source.child_elements;
  }
  return [];
}

function isEditorSentinelComponent(component: any): boolean {
  if (!component || typeof component !== "object") return false;
  return component.id === -11 || component.type === "editor";
}

function isEditorSentinelRelation(relation: any): boolean {
  if (!relation || typeof relation !== "object") return false;
  return relation.child_id === -11;
}

function buildTemplateNodeIndex(
  source: any,
  index: Map<string, any>,
  visited: Set<any>
): void {
  if (!source || typeof source !== "object" || visited.has(source)) {
    return;
  }
  if (isEditorSentinelComponent(source)) {
    return;
  }
  visited.add(source);
  const sourceUuid =
    typeof source.uuid === "string" ? source.uuid.trim() : "";
  if (sourceUuid) {
    index.set(sourceUuid, source);
  }
  for (const child of toTemplateChildren(source).filter(
    (x) => !isEditorSentinelComponent(x)
  )) {
    buildTemplateNodeIndex(child, index, visited);
  }
  if (source.components && typeof source.components === "object") {
    Object.values(source.components).forEach((child) => {
      buildTemplateNodeIndex(child, index, visited);
    });
  }
}

function getTemplateTreeWeight(source: any): number {
  if (!source || typeof source !== "object") {
    return -1;
  }
  const children = toTemplateChildren(source);
  return children.reduce(
    (sum, child) => sum + 1 + Math.max(getTemplateTreeWeight(child), 0),
    0
  );
}

function getTemplateRootFromComponentsMap(
  template: TemplateLike
): AllComponents | null {
  const componentsMap =
    template.components && typeof template.components === "object"
      ? template.components
      : null;
  if (!componentsMap || Object.keys(componentsMap).length === 0) {
    return null;
  }

  const byUuid = new Map<string, AllComponents>();
  const childUuids = new Set<string>();
  for (const component of Object.values(componentsMap)) {
    if (!component) continue;
    const uuid = component.uuid?.trim();
    if (uuid) {
      byUuid.set(uuid, component);
    }
    for (const relation of component.childWebpageComponentRelations ?? []) {
      const childUuid =
        typeof relation?.child_uuid === "string" ? relation.child_uuid.trim() : "";
      if (childUuid) {
        childUuids.add(childUuid);
      }
    }
  }

  const preferredUuidCandidates = [
    template.rootComponentUuid?.trim() ?? "",
    template.component?.uuid?.trim() ?? "",
  ].filter((x) => !!x);

  for (const candidate of preferredUuidCandidates) {
    const component = byUuid.get(candidate);
    if (component) return component;
  }

  for (const [uuid, component] of byUuid.entries()) {
    if (!childUuids.has(uuid)) {
      return component;
    }
  }

  return Object.values(componentsMap).find((x) => !!x) ?? null;
}

function normalizeTemplateTree(source: any): any {
  if (!source || typeof source !== "object") {
    return source;
  }
  const index = new Map<string, any>();
  buildTemplateNodeIndex(source, index, new Set<any>());
  const visitedUuids = new Set<string>();

  const hydrate = (node: any): any => {
    if (!node || typeof node !== "object") {
      return node;
    }
    const nodeUuid = typeof node.uuid === "string" ? node.uuid.trim() : "";
    if (nodeUuid && visitedUuids.has(nodeUuid)) {
      return { ...node, childElements: [] };
    }
    if (nodeUuid) {
      visitedUuids.add(nodeUuid);
    }

    const directChildren = toTemplateChildren(node).filter(
      (x) => !isEditorSentinelComponent(x)
    );
    const relations = Array.isArray(node.childWebpageComponentRelations)
      ? node.childWebpageComponentRelations
      : [];
    const filteredRelations = relations.filter(
      (relation) => !isEditorSentinelRelation(relation)
    );
    const relationChildren =
      directChildren.length > 0
        ? []
        : filteredRelations
            .map((relation) => {
              const childUuid =
                typeof relation?.child_uuid === "string"
                  ? relation.child_uuid.trim()
                  : "";
              if (!childUuid) return null;
              return index.get(childUuid) ?? null;
            })
            .filter((x): x is any => !!x);
    const resolvedChildren = (directChildren.length > 0
      ? directChildren
      : relationChildren
    ).map((child) => hydrate(child));

    return {
      ...node,
      childElements: resolvedChildren,
      childWebpageComponentRelations:
        filteredRelations.length > 0
          ? filteredRelations
          : resolvedChildren.map((child, index) => ({
              id: 0,
              parent_id: node.id ?? null,
              parent_uuid: node.uuid?.trim() ?? null,
              child_id: child?.id ?? null,
              child_uuid: child?.uuid?.trim() ?? null,
              sort: index + 1,
              created_at: null,
              updated_at: null,
            })),
    };
  };

  const normalized = hydrate(source);
  const childElements = toTemplateChildren(normalized).map((child) =>
    normalizeTemplateTree(child)
  );
  return {
    ...normalized,
    childElements,
    childWebpageComponentRelations:
      Array.isArray(normalized.childWebpageComponentRelations) &&
      normalized.childWebpageComponentRelations.length > 0
        ? normalized.childWebpageComponentRelations
        : childElements.map((child, index) => ({
            id: 0,
            parent_id: normalized.id ?? null,
            parent_uuid: normalized.uuid?.trim() ?? null,
            child_id: child?.id ?? null,
            child_uuid: child?.uuid?.trim() ?? null,
            sort: index + 1,
            created_at: null,
            updated_at: null,
          })),
  };
}

export function getTemplateSourceComponent(
  template: TemplateLike | null | undefined
): AllComponents | null {
  if (!template) return null;
  const mapRootSource = getTemplateRootFromComponentsMap(template);
  const componentSource = template.component;
  const preferred =
    getTemplateTreeWeight(mapRootSource) >= getTemplateTreeWeight(componentSource)
      ? mapRootSource
      : componentSource;
  if (!preferred) {
    return null;
  }
  const sourceWithMap =
    mapRootSource && preferred === mapRootSource
      ? { ...preferred, components: template.components ?? {} }
      : preferred;
  const hydrated = normalizeTemplateTree(sourceWithMap) as AllComponents;
  return hydrated;
}

function getTemplateWebpageComponentKey(
  template: TemplateLike | null | undefined
): string | null {
  if (!template) return null;
  const sourceComponent = getTemplateSourceComponent(template);
  if (template.rootComponentUuid?.trim()) {
    return template.rootComponentUuid;
  }
  if (sourceComponent?.uuid?.trim()) {
    return sourceComponent.uuid;
  }
  return null;
}

export function getTemplateLinkedComponent(
  components: { [key: string]: AllComponents } | undefined,
  template: TemplateLike | null | undefined
): AllComponents | undefined {
  if (!components || !template) return undefined;
  const sourceComponent = getTemplateSourceComponent(template);
  const key = getTemplateWebpageComponentKey(template);
  if (key && components[key]) {
    return components[key];
  }
  if (sourceComponent?.uuid && components[sourceComponent.uuid]) {
    return components[sourceComponent.uuid];
  }
  return undefined;
}

export function findTemplateByStoredReference(
  templates: Iterable<TemplateLike>,
  storedId: number | string | null | undefined
): TemplateLike | undefined {
  if (storedId === null || storedId === undefined) return undefined;
  const list = Array.isArray(templates) ? templates : Array.from(templates);
  const value = String(storedId);
  return list.find((template) => {
    if (template.id != null && String(template.id) === value) return true;
    if (
      template.rootComponentUuid &&
      template.rootComponentUuid === value
    ) {
      return true;
    }
    return false;
  });
}

export function templateReferencesComponent(
  template: TemplateLike,
  componentId: string | number
): boolean {
  const sourceComponent = getTemplateSourceComponent(template);
  const value = String(componentId);
  if (template.rootComponentUuid && template.rootComponentUuid === value) {
    return true;
  }
  if (
    template.rootComponentUuid &&
    template.rootComponentUuid === value
  ) {
    return true;
  }
  return false;
}
